// src/features/transfers/transfersApi.ts
import { api } from "../auth/authApi";
import { downloadBlob } from "../analytics/analyticsApi";
import type {
  Paginated,
  UUID,
  Transfer,
  TransferCreateUpdate,
  TransferListParams,
  TransferStatus,
} from "./types";

const cleanParams = (params: Record<string, any>) => {
  const out: Record<string, any> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (typeof v === "string" && v.trim() === "") return;
    out[k] = v;
  });
  return out;
};

export const transfersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // GET /transfers?date=&date_from=&date_to=&status=&provider=&client=&search=&ordering=&page=
    listTransfers: builder.query<Paginated<Transfer>, TransferListParams | void>({
      query: (params) => ({
        url: "/transfers/",
        method: "GET",
        params: cleanParams(params || {}),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map((t: Transfer) => ({ type: "Transfers" as const, id: t.id })),
              { type: "Transfers" as const, id: "LIST" },
            ]
          : [{ type: "Transfers" as const, id: "LIST" }],
    }),

    getTransfer: builder.query<Transfer, UUID>({
      query: (id) => ({ url: `/transfers/${id}/`, method: "GET" }),
      providesTags: (_r, _e, id) => [{ type: "Transfers", id }],
    }),

    createTransfer: builder.mutation<Transfer, TransferCreateUpdate>({
      query: (body) => ({ url: "/transfers/", method: "POST", body }),
      invalidatesTags: [{ type: "Transfers", id: "LIST" }],
    }),

    updateTransfer: builder.mutation<Transfer, { id: UUID; patch: Partial<TransferCreateUpdate> }>({
      query: ({ id, patch }) => ({ url: `/transfers/${id}/`, method: "PATCH", body: patch }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Transfers", id: arg.id },
        { type: "Transfers", id: "LIST" },
      ],
    }),

    deleteTransfer: builder.mutation<void, { id: UUID }>({
      query: ({ id }) => ({ url: `/transfers/${id}/`, method: "DELETE" }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Transfers", id: arg.id },
        { type: "Transfers", id: "LIST" },
      ],
    }),

    archiveTransfer: builder.mutation<{ is_archived: boolean }, { id: UUID }>({
      query: ({ id }) => ({ url: `/transfers/${id}/archive/`, method: "POST" }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Transfers", id: arg.id },
        { type: "Transfers", id: "LIST" },
      ],
    }),

    // POST /transfers/{id}/status  body: {status}
    setTransferStatus: builder.mutation<Transfer, { id: UUID; status: TransferStatus }>({
      query: ({ id, status }) => ({
        url: `/transfers/${id}/status/`,
        method: "POST",
        body: { status },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Transfers", id: arg.id },
        { type: "Transfers", id: "LIST" },
      ],
    }),

    // GET /transfers/export.xlsx/ — same params as list, returns Blob
    exportTransfersXlsx: builder.mutation<Blob, TransferListParams | void>({
      query: (params) => ({
        url: "/transfers/export.xlsx/",
        method: "GET",
        params: cleanParams(params || {}),
        responseHandler: (response) => response.blob(),
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data: blob } = await queryFulfilled;
          const dateFrom = arg?.date_from ?? "";
          const dateTo = arg?.date_to ?? "";
          const filename =
            dateFrom && dateTo
              ? `transfers_export_${dateFrom}_${dateTo}.xlsx`
              : "transfers_export.xlsx";
          downloadBlob(blob, filename);
        } catch {
          // Error surfaced by mutation state / snackbar in UI
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useListTransfersQuery,
  useGetTransferQuery,
  useCreateTransferMutation,
  useUpdateTransferMutation,
  useDeleteTransferMutation,
  useArchiveTransferMutation,
  useSetTransferStatusMutation,
  useExportTransfersXlsxMutation,
} = transfersApi;
